export type DiscrepancyFlag =
  | "SESSION_COUNT_UNDER_RECORDED"
  | "SESSION_COUNT_OVER_RECORDED"
  | "ACTIVE_PAST_END_DATE"
  | "ACTIVE_OVERUTILIZED"
  | "COMPLETED_WITH_REMAINING"
  | "INCORRECT_SESSION_BUNDLE_MAPPED";

export type FixableStatus = "ACTIVE" | "COMPLETED" | "EXPIRED";

export interface FlagConfig {
  label: string;
  className: string;
  fix?: { action: string; newStatus?: FixableStatus };
}

export const FLAG_CONFIG: Record<DiscrepancyFlag, FlagConfig> = {
  SESSION_COUNT_UNDER_RECORDED: {
    label: "Session Count Low",
    className: "bg-blue-100 text-blue-800 border-blue-200",
    fix: { action: "Sync session count" },
  },
  SESSION_COUNT_OVER_RECORDED: {
    label: "Session Count High",
    className: "bg-yellow-100 text-yellow-800 border-yellow-200",
    fix: { action: "Link orphaned sessions" },
  },
  ACTIVE_PAST_END_DATE: {
    label: "Active Past End Date",
    className: "bg-red-100 text-red-800 border-red-200",
    fix: { action: "Mark as EXPIRED", newStatus: "EXPIRED" },
  },
  ACTIVE_OVERUTILIZED: {
    label: "Active Past Completion",
    className: "bg-orange-100 text-orange-800 border-orange-200",
    fix: { action: "Mark as COMPLETED", newStatus: "COMPLETED" },
  },
  COMPLETED_WITH_REMAINING: {
    label: "Completed Early",
    className: "bg-purple-100 text-purple-800 border-purple-200",
    fix: { action: "Reopen bundle" },
  },
  INCORRECT_SESSION_BUNDLE_MAPPED: {
    label: "Incorrect Session-Bundle Mapped",
    className: "bg-rose-100 text-rose-800 border-rose-200",
  },
};

/** Returns the target status to apply for a set of flags.
 *  EXPIRED takes priority over COMPLETED when both apply. */
export function resolveFixStatus(
  flags: DiscrepancyFlag[]
): FixableStatus | null {
  if (flags.includes("ACTIVE_PAST_END_DATE")) return "EXPIRED";
  if (flags.includes("ACTIVE_OVERUTILIZED")) return "COMPLETED";
  return null;
}

/**
 * For SESSION_COUNT_UNDER_RECORDED: actual > utilized, safe to sync up.
 * Returns the target count, or null if this flag is not present.
 */
export function resolveSessionCountFix(row: BundleDiscrepancy): number | null {
  if (!row.discrepancy_flags.includes("SESSION_COUNT_UNDER_RECORDED")) return null;
  return row.actual_session_count;
}

/**
 * For SESSION_COUNT_OVER_RECORDED: computes the fix given how many orphaned sessions will be linked.
 * Linking sessions reconciles actual with the existing utilized count — utilized is NOT changed.
 * - postActual = actual + selectedCount  (what actual will look like after linking)
 * - newStatus  = COMPLETED if active bundle fills up, EXPIRED if active & past end date, else null
 */
export function resolveOverRecordedFix(
  row: BundleDiscrepancy,
  selectedCount: number,
): { postActual: number; newStatus: FixableStatus | null } {
  const postActual = row.actual_session_count + selectedCount;
  let newStatus: FixableStatus | null = null;
  if (row.status === "ACTIVE") {
    if (postActual >= row.total_session_quantity) {
      newStatus = "COMPLETED";
    } else if (new Date(row.end_date) < new Date()) {
      newStatus = "EXPIRED";
    }
  }
  return { postActual, newStatus };
}

/**
 * For COMPLETED_WITH_REMAINING: computes the fix given how many sessions will be mapped.
 * - newUtilized  = utilized + mappedCount
 * - newStatus:
 *     COMPLETED if newUtilized >= total
 *     EXPIRED   if newUtilized < total AND end_date is in the past
 *     ACTIVE    if newUtilized < total AND end_date is in the future
 */
export function resolveCompletedEarlyFix(
  row: BundleDiscrepancy,
  mappedCount: number,
): { newUtilized: number; newStatus: FixableStatus } {
  const newUtilized = row.utilized_session_quantity + mappedCount;
  let newStatus: FixableStatus;
  if (newUtilized >= row.total_session_quantity) {
    newStatus = "COMPLETED";
  } else if (new Date(row.end_date) < new Date()) {
    newStatus = "EXPIRED";
  } else {
    newStatus = "ACTIVE";
  }
  return { newUtilized, newStatus };
}

/** True if the row has at least one flag with a defined fix action. */
export function isFixable(row: BundleDiscrepancy): boolean {
  return row.discrepancy_flags.some((flag) => FLAG_CONFIG[flag].fix !== undefined);
}

export interface BundleDiscrepancy {
  id: string;
  status: string;
  start_date: string;
  end_date: string;
  total_session_quantity: number;
  utilized_session_quantity: number;
  actual_session_count: number;
  bundle_id: string;
  bundle_name: string;
  users: { id: string; full_name: string }[];
  discrepancy_flags: DiscrepancyFlag[];
}

export interface BundleDiscrepanciesResponse {
  discrepancies: BundleDiscrepancy[];
  total: number;
  page: number;
  lastPage: number;
  nextPage: number | null;
}
